import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import _ from "lodash";
import { FC, useCallback, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useDynamicFormsTranslate, useSelectItemsTranslate } from "@ryde/redux";
import {
  bulkMultiselectActionsSelectItems,
  checkCleanupNestedValidation,
  errors as errorsConst,
  excludeNotArrayValue,
  getBulkFormFieldName,
  getBulkMultiselectValueFormValidation,
  ICustomField,
  Log,
  requiredRuleStringField,
  ValidationHelpers,
} from "@ryde/shared";

import { BulkActionFieldStackStyled } from "./bulk-action-field-stack.styled";
import { DropdownTemplate } from "./dropdown.template";
import { getDropdownValuesLabels, ListBox } from "./dropdowns";
import { SelectAllPaperWrapper } from "./selectall-paper-wrapper.template";
import { useClearBulkMultiselectOnAction } from "./use-clear-bulk-multiselect-on-action";
import { useIsBulkControlSelected } from "./use-is-bulk-control-selected";

export const BulkMultiselectTemplate: FC<ICustomField> = ({
  id,
  name,
  formAttributes,
  formLabel,
  labelTranslated,
  e2eData,
}) => {
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const isBulkControlSelected = useIsBulkControlSelected(name);
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const {
    selectItems,
    disabled,
    skipDropdownItemsTranslation,
    disableSorting,
  } = formAttributes || {};
  const initial: number[] = getValues(name as string);

  if (initial && !_.isArray(initial)) {
    Log.error(t(errorsConst.form.multiSelectBoundTemplateInitialValueNotArray));
  }

  const { translateLabel } = useSelectItemsTranslate();
  const [options, mapOptionLabel] = useMemo(() => {
    return getDropdownValuesLabels({
      dropDownItems: selectItems,
      transformLabelFn: skipDropdownItemsTranslation
        ? undefined
        : ({ label }) => translateLabel(label),
    });
  }, [selectItems, skipDropdownItemsTranslation, translateLabel]);

  const getOptionLabel = useCallback(
    (value: unknown) => String(mapOptionLabel.get(value as string | number)),
    [mapOptionLabel],
  );

  const selectAll = (): void => {
    const isSelectAll = getValues(name)?.length !== options?.length;
    setValue(name, isSelectAll ? options : [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const bulkAction = watch(getBulkFormFieldName(name));
  useClearBulkMultiselectOnAction(name);
  const multiselectFormValidation = getBulkMultiselectValueFormValidation(
    isBulkControlSelected,
    bulkAction,
  );

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(multiselectFormValidation);
  }, [multiselectFormValidation]);

  const {
    field: { value: formValue, onChange, ...restField },
    fieldState: { error },
  } = useController({ name, control });

  const sortedValue = useMemo(() => {
    const currentSelection = excludeNotArrayValue(formValue);
    if (disableSorting) {
      return currentSelection;
    }
    return currentSelection.toSorted((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [formValue, disableSorting, getOptionLabel]);

  const value = useMemo(() => sortedValue, [sortedValue]);

  return (
    <FormControl
      fullWidth
      required={requiredComputed}
      data-e2e-template="multiselect-template"
      data-e2e-field-name={name}
      error={!!error}
    >
      <BulkActionFieldStackStyled>
        <DropdownTemplate
          name={getBulkFormFieldName(name)}
          labelTranslated={t("general.multiselectBulkAction")}
          formValidation={
            isBulkControlSelected ? requiredRuleStringField : undefined
          }
          formAttributes={{
            selectItems: bulkMultiselectActionsSelectItems,
          }}
        />
        <Autocomplete
          {...restField}
          data-e2e={e2eData}
          data-e2e-id={`${name}_${id || ""}`}
          multiple
          disabled={disabled}
          options={options || []}
          value={value}
          slotProps={{
            listbox: {
              component: ListBox,
            },
          }}
          onChange={(_event, newValue) => {
            if (!newValue) {
              return;
            }

            checkCleanupNestedValidation(name, errors);

            onChange(newValue);
          }}
          getOptionLabel={getOptionLabel}
          disableCloseOnSelect
          renderInput={params => (
            <TextField
              {...params}
              error={!!error}
              label={formLabelTranslated}
              disabled={disabled}
              required={requiredComputed}
            />
          )}
          slots={{
            paper: props => (
              <SelectAllPaperWrapper
                {...props}
                selectAll={formValue?.length === options?.length}
                handleToggleSelectAll={() => {
                  selectAll();
                }}
              />
            ),
          }}
          filterSelectedOptions
        />
      </BulkActionFieldStackStyled>
      {error && (
        <FormHelperText data-e2e="multiselect-template-error">
          {error.message}
        </FormHelperText>
      )}
    </FormControl>
  );
};
